import React, { useEffect, useState } from 'react';
import Loading from "../../component/Loading/Loading";
import Pagination from '../../utils/Pagenation/Pagenation';
import useAxiosInstance from '../../services/useAxiosInstance';
import { Search, Calendar, Filter } from 'lucide-react';

function AuditLog() {
  const axiosInstance = useAxiosInstance();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [communities, setCommunities] = useState([]);

  const formatDate = (date) => {
    const d = new Date(date);
    const month = '' + (d.getMonth() + 1);
    const day = '' + d.getDate();
    const year = d.getFullYear();

    return [year, month.padStart(2, '0'), day.padStart(2, '0')].join('-');
  };

  const today = formatDate(new Date());
  const [startDate, setStartDate] = useState(today);
  const [selectedCommunityId, setSelectedCommunityId] = useState(0);

  const [filterData, setFilterData] = useState({
    startDates: today,
    selectedCommunityIds: 0
  });

  const handleStartDateChange = (event) => {
    setFilterData({
      ...filterData,
      startDates: event.target.value
    });
  };

  const handleCommunityChange = (event) => {
    setFilterData({
      ...filterData,
      selectedCommunityIds: event.target.value
    });
  };

  const handleSearch = () => {
    setStartDate(filterData.startDates);
    setSelectedCommunityId(parseInt(filterData.selectedCommunityIds));
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setStartDate(today);
    setSelectedCommunityId(0);
    setSearchTerm('');
    setFilterData({
      startDates: today,
      selectedCommunityIds: 0
    });
  };

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/public/auditlog/DateAndCommunityId`, {
        params: {
          createdDate: startDate || 0,
          communityId: parseInt(selectedCommunityId) || 0,
        }
      });
      setData(response.data);
      setCurrentPage(0);
    } catch (error) {
      console.error('There was an error fetching the data!', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [startDate, selectedCommunityId]);

  useEffect(() => {
    const fetchCommunities = async () => {
      try {
        const response = await axiosInstance.get(`/api/public/community/getAll`);
        setCommunities(response.data);
      } catch (error) {
        console.error('There was an error fetching the community list!', error);
      }
    };

    fetchCommunities();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const filteredData = data.filter(item => {
    const searchMatch = (item.userName && item.userName.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.communityId && item.communityId.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.pageName && item.pageName.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.actionPerformed && item.actionPerformed.toString().toLowerCase().includes(searchTerm.toLowerCase()));

    const communityMatch = selectedCommunityId === 0 || item.communityId === selectedCommunityId;

    return searchMatch && communityMatch;
  });

  const pageCount = Math.ceil(filteredData.length / pageSize);
  const startIndex = currentPage * pageSize;
  const visibleData = filteredData.slice(startIndex, startIndex + pageSize);

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Audit Log</h1>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="p-4 border-b border-gray-200">
              {/* Mobile-first responsive design */}
              <div className="flex flex-col md:flex-row md:justify-between space-y-3 md:space-y-0 md:space-x-3">
                {/* Search input - Full width on all screens */}
                <div className="w-full md:w-64">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="searchName"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Date input */}
                <div className="w-full md:w-auto">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Calendar className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      name="startDates"
                      value={filterData.startDates}
                      onChange={handleStartDateChange}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Community selector */}
                <div className="w-full md:w-auto md:flex-grow">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 h-full pointer-events-none">
                      <Filter className="h-4 w-4 text-gray-400" />
                    </div>
                    <select
                      id="selectedCommunityIds"
                      value={filterData.selectedCommunityIds}
                      onChange={handleCommunityChange}
                      className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-md w-full md:w-fit focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={0}>Select a Community</option>
                      {communities.map((community) => (
                        <option
                          key={community.communityId}
                          value={community.communityId}
                        >
                          {community.communityName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Search button - Full width on mobile, auto width on desktop */}
                <button
                  onClick={handleSearch}
                  className="w-full md:w-auto px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 whitespace-nowrap"
                >
                  Search
                </button>

                {/* Clear button - Only shown when filters are active */}
                {(selectedCommunityId !== 0 || searchTerm) && (
                  <button
                    onClick={clearFilters}
                    className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                    title="Clear Filter"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-400">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">User Name</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Community Name</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Created Date</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Page Name</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Action performed</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {visibleData.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                    </tr>
                  ) : (
                    visibleData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.userName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.communityName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(item.createdDate).toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.pageName.substring(5)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.actionPerformed}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing {visibleData.length} of {filteredData.length} entries
              </div>
              <div className="flex-1 flex justify-end">
                <Pagination
                  currentPage={currentPage}
                  pageCount={pageCount}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuditLog;